import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import MainContainer from "../../layout/MainContainer";

import { Box, IconButton } from "@mui/material";
import SearchBar from "../../components/SearchBar";
import { GridColDef } from "@mui/x-data-grid";
import TableComponent from "../../components/TableComponent";
import { CommonBody, Permission, VisaType } from "../../types/General";
import { useVisaTypesListMutation } from "../../service/visaType";
import { errorToast } from "../../helpers";
import { generateEncryptedKeyBody } from "../../utils/crypto";
import { isValidInput } from "../../utils/validations";
import { USER_ROLES } from "../../utils/enums";
import useAuth from "../../hooks/useAuth";

const VisaTypes = () => {
  const navigate = useNavigate();
  const userData = useAuth();
  const [hidePermission, setHidePermission] = useState<Permission>();
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>("");

  const [visaTypeList] = useVisaTypesListMutation();

  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState<VisaType[]>([]);
  const [page, setPage] = useState<number>(1);

  const handlePageChange = (event: any, newPage: number) => {
    setPage(newPage + 1);
  };

  const columns: GridColDef<(typeof rows)[number]>[] = [
    {
      flex: 1,
      minWidth: 100,
      field: "image",
      headerName: "Light Mode Image",
      editable: false,
      sortable: false,
      renderCell: ({ row }) => {
        return (
          <div className="sq_image">
            <figure>
              <img
                src={
                  row?.visa_type_image?.media_url
                    ? row?.visa_type_image?.media_url
                    : "/static/images/picture.png"
                }
                alt=""
                style={{ objectFit: "contain" }}
              />
            </figure>
          </div>
        );
      },
    },
    {
      flex: 1,
      minWidth: 100,
      field: "dark_image",
      headerName: "Dark Mode Image",
      editable: false,
      sortable: false,
      renderCell: ({ row }) => {
        return (
          <div className="sq_image">
            <figure>
              <img
                src={
                  row?.visa_type_image_dark?.media_url
                    ? row?.visa_type_image_dark?.media_url
                    : "/static/images/picture.png"
                }
                alt=""
                style={{ objectFit: "contain" }}
              />
            </figure>
          </div>
        );
      },
    },
    {
      flex: 1,
      minWidth: 100,
      field: "visa_type",
      headerName: "Visa Type",
      editable: false,
      sortable: false,
    },

    {
      flex: 0.1,
      minWidth: 80,
      field: "action",
      headerName: "Action",
      editable: false,
      sortable: false,
      renderCell: ({ row }) => {
        return (
          <div className="table_actions">
            <Box>
              <IconButton onClick={() => navigate(`edit/${row?.id}`)}>
                <img src="/static/images/edit2_icon.svg" alt="" />
              </IconButton>
            </Box>
          </div>
        );
      },
    },
  ];

  const fetchData = async () => {
    setLoading(true);
    try {
      const body = {
        start: page,
        length: 10,
        search: debouncedSearchTerm.trim(),
      };

      const encryptedBody = generateEncryptedKeyBody(body) as CommonBody;
      const response = await visaTypeList(encryptedBody).unwrap();
      setLoading(false);

      if (response?.status === 200) {
        setRows(response?.data?.response_object || []);
        setCount(response?.data?.total_records);
      } else {
        setRows([]);
      }
    } catch (error: any) {
      errorToast(error?.data?.message || "");
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [debouncedSearchTerm, page]);

  const checkPermission = () => {
    const permission = userData?.role_permission;
    if (permission?.length) {
      const foundPermission = permission?.find(
        (ele: Permission) => ele?.module === "Visa Type"
      );

      if (foundPermission) {
        setHidePermission(foundPermission);
      } else {
        navigate(-1);
      }
    }
  };

  useEffect(() => {
    if (userData?.user_role_type === USER_ROLES.SUBADMIN) {
      checkPermission();
    }
  }, [userData]);

  useEffect(() => {
    if (typeof hidePermission !== "undefined") {
      if (!hidePermission?.can_view) {
        navigate("/permission-denied", { replace: true });
      }
    }
  }, [hidePermission, userData]);

  return (
    <MainContainer>
      <div className="main_title">
        <h1>Visa Types</h1>
        <p>
          <span onClick={() => navigate("/dashboard")}>Dashboard</span> - Visa
          Types
        </p>
      </div>

      <div className="cards">
        <Box className="custom_tabs" sx={{ width: "100%" }}>
          <div className="table_header">
            <div className="left_s">
              <Box className="search_box">
                <SearchBar
                  searchTerm={searchTerm}
                  placeholder="Search"
                  setDebouncedSearchTerm={setDebouncedSearchTerm}
                  value={searchTerm}
                  onCross={() => setSearchTerm("")}
                  onChange={(val: any) => {
                    if (isValidInput(val.target.value)) {
                      setSearchTerm(val.target.value);
                    }
                  }}
                />
              </Box>
            </div>
          </div>

          <div className="sorting_table">
            <TableComponent
              columns={columns}
              rows={rows}
              count={count}
              page={page}
              onPageChange={handlePageChange}
              loading={loading}
            />
          </div>
        </Box>
      </div>
    </MainContainer>
  );
};

export default VisaTypes;
